import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import type { Role, User, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { describeRejection } from "@/modules/settings/errors";

const USERS_QUERY_KEY = ["settings", "users"];

function statusVariant(
  status: User["status"],
): "default" | "secondary" | "destructive" {
  if (status === "active") return "default";
  if (status === "invited") return "secondary";
  return "destructive";
}

function formatDate(value: string | null): string {
  if (value === null) return "Never";
  return new Date(value).toLocaleString();
}

/**
 * One user, opened from `UsersList` in a `RecordDrawer` (M10-7, M15-2).
 *
 * `user` is looked up on the page that opened it, the same simplification
 * `TransfersList` documents for its own selection — `SettingsRepository` has
 * no `getUser(id)`, and the users this ERP manages number in the tens, not
 * the thousands, so the list's own page is always where the row came from.
 */
export function UserDetail({ user, roles }: { user: User; roles: Role[] }) {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const [roleDraft, setRoleDraft] = useState<string[] | null>(null);
  const selectedRoleIds = roleDraft ?? user.roleIds;
  const dirty =
    roleDraft !== null &&
    (roleDraft.length !== user.roleIds.length ||
      roleDraft.some((id) => !user.roleIds.includes(id)));

  const [rolesRejection, setRolesRejection] = useState<WriteRejection | null>(
    null,
  );
  const [statusRejection, setStatusRejection] = useState<WriteRejection | null>(
    null,
  );
  const [resendResult, setResendResult] = useState<
    { emailSent: boolean } | WriteRejection | null
  >(null);

  function invalidateUsers() {
    void queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
  }

  const rolesMutation = useMutation({
    mutationFn: (roleIds: string[]) =>
      data.settings.setUserRoles(user.id, roleIds),
    onSuccess: (result) => {
      if (result.ok) {
        setRoleDraft(null);
        setRolesRejection(null);
        invalidateUsers();
      } else {
        setRolesRejection(result.rejection);
      }
    },
  });

  const suspendMutation = useMutation({
    mutationFn: () => data.settings.suspendUser(user.id),
    onSuccess: (result) => {
      if (result.ok) {
        setStatusRejection(null);
        invalidateUsers();
      } else {
        setStatusRejection(result.rejection);
      }
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => data.settings.reactivateUser(user.id),
    onSuccess: (result) => {
      if (result.ok) {
        setStatusRejection(null);
        invalidateUsers();
      } else {
        setStatusRejection(result.rejection);
      }
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => data.settings.resendInvite(user.id),
    onSuccess: (result) => {
      if (result.ok) {
        setResendResult({ emailSent: result.data.emailSent });
        invalidateUsers();
      } else {
        setResendResult(result.rejection);
      }
    },
  });

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold">{user.name}</h3>
          <Badge variant={statusVariant(user.status)} className="capitalize">
            {user.status}
          </Badge>
        </div>
        <p className="text-muted-foreground text-sm">{user.email}</p>
        <p className="text-muted-foreground text-xs">
          Last sign-in: {formatDate(user.lastLoginAt)}
        </p>
      </div>

      <div className="space-y-2">
        <Label>Roles</Label>
        <div className="space-y-2 rounded-md border p-3">
          {roles.length === 0 ? (
            <p className="text-muted-foreground text-sm">No roles exist yet.</p>
          ) : (
            roles.map((role) => (
              <div key={role.id} className="flex items-center gap-2">
                <Checkbox
                  id={`user-${user.id}-role-${role.id}`}
                  checked={selectedRoleIds.includes(role.id)}
                  onCheckedChange={(checked) => {
                    const base = roleDraft ?? user.roleIds;
                    setRoleDraft(
                      checked
                        ? [...base, role.id]
                        : base.filter((id) => id !== role.id),
                    );
                  }}
                />
                <Label
                  htmlFor={`user-${user.id}-role-${role.id}`}
                  className="text-sm font-normal"
                >
                  {role.name}
                </Label>
              </div>
            ))
          )}
        </div>
        {rolesRejection ? (
          <p className="text-destructive text-xs">
            {describeRejection(rolesRejection)}
          </p>
        ) : null}
        {dirty ? (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={rolesMutation.isPending}
              onClick={() => {
                rolesMutation.mutate(selectedRoleIds);
              }}
            >
              {rolesMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Save roles
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setRoleDraft(null);
                setRolesRejection(null);
              }}
            >
              Revert
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-2 border-t pt-4">
        <Label>Account</Label>
        {statusRejection ? (
          <p className="text-destructive text-xs">
            {describeRejection(statusRejection)}
          </p>
        ) : null}
        {resendResult ? (
          <p
            className={
              "text-xs " +
              ("emailSent" in resendResult && !resendResult.emailSent
                ? "text-destructive"
                : "text-muted-foreground")
            }
          >
            {"emailSent" in resendResult
              ? resendResult.emailSent
                ? "Invite email sent."
                : "The account was updated, but the invite email failed to send."
              : describeRejection(resendResult)}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {user.status === "invited" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={resendMutation.isPending}
              onClick={() => {
                resendMutation.mutate();
              }}
            >
              {resendMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Resend invite
            </Button>
          ) : null}
          {user.status === "active" ? (
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={suspendMutation.isPending}
              onClick={() => {
                suspendMutation.mutate();
              }}
            >
              {suspendMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Suspend
            </Button>
          ) : null}
          {user.status === "suspended" ? (
            <Button
              type="button"
              size="sm"
              disabled={reactivateMutation.isPending}
              onClick={() => {
                reactivateMutation.mutate();
              }}
            >
              {reactivateMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : null}
              Reactivate
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
